import type { LookupByUpcResponse, UpcCatalogMatch } from '@erp/rpc';

import type { DailyReportFileResult, FlaggedPotfUpcRow } from './types';

export const UPC_LOOKUP_CHUNK_SIZE = 200;

export function normalizeUpc(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function uniqueNormalizedUpcs(
  rows: readonly Pick<FlaggedPotfUpcRow, 'upcCode'>[],
): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const row of rows) {
    const upc = normalizeUpc(row.upcCode);
    if (!upc || seen.has(upc)) continue;
    seen.add(upc);
    unique.push(upc);
  }
  return unique;
}

export function isOwnCatalogPart(
  match: UpcCatalogMatch,
  productNumber: string,
): boolean {
  const own = productNumber.trim().toUpperCase();
  if (own.length === 0) return false;
  return (
    match.partNumber.trim().toUpperCase() === own ||
    match.productKey.trim().toUpperCase() === own
  );
}

export function formatUpcSiblingPart(match: UpcCatalogMatch): string {
  return match.isActive ? match.partNumber : `${match.partNumber} (inactive)`;
}

export function formatUpcSiblingDivisions(match: UpcCatalogMatch): string {
  return match.divisions.join('/');
}

export function formatUpcSiblingColumns(
  matches: readonly UpcCatalogMatch[],
): Pick<FlaggedPotfUpcRow, 'otherPartNumber' | 'otherDivisions'> {
  return {
    otherPartNumber: matches.map(formatUpcSiblingPart).join('; '),
    otherDivisions: matches.map(formatUpcSiblingDivisions).join('; '),
  };
}

export function enrichPotfUpcRow(
  row: FlaggedPotfUpcRow,
  matches: readonly UpcCatalogMatch[],
): FlaggedPotfUpcRow {
  const others = matches.filter(
    (match) => !isOwnCatalogPart(match, row.productNumber),
  );
  return { ...row, ...formatUpcSiblingColumns(others) };
}

export function enrichPotfUpcRows(
  rows: readonly FlaggedPotfUpcRow[],
  byUpc: Readonly<Record<string, UpcCatalogMatch[]>>,
): FlaggedPotfUpcRow[] {
  return rows.map((row) => {
    const upc = normalizeUpc(row.upcCode);
    return enrichPotfUpcRow(row, upc ? (byUpc[upc] ?? []) : []);
  });
}

export async function loadUpcCatalogMatches(
  upcs: readonly string[],
  lookup: (upcs: string[]) => Promise<LookupByUpcResponse['byUpc']>,
): Promise<LookupByUpcResponse['byUpc']> {
  const pages = await Promise.all(
    Array.from(
      { length: Math.ceil(upcs.length / UPC_LOOKUP_CHUNK_SIZE) },
      (_, page) =>
        lookup(
          upcs.slice(
            page * UPC_LOOKUP_CHUNK_SIZE,
            (page + 1) * UPC_LOOKUP_CHUNK_SIZE,
          ),
        ),
    ),
  );
  return Object.assign({}, ...pages);
}

export async function enrichPotfUpcResults(
  results: readonly DailyReportFileResult[],
  lookup: (upcs: string[]) => Promise<LookupByUpcResponse['byUpc']>,
): Promise<DailyReportFileResult[]> {
  const potfRows = results.flatMap((result) =>
    result.status === 'analyzed' && result.kind === 'potfUpc'
      ? result.flagged
      : [],
  );
  if (potfRows.length === 0) return [...results];

  const upcs = uniqueNormalizedUpcs(potfRows);
  let byUpc: LookupByUpcResponse['byUpc'] = {};
  if (upcs.length > 0) {
    try {
      byUpc = await loadUpcCatalogMatches(upcs, lookup);
    } catch {
      byUpc = {};
    }
  }

  return results.map((result) => {
    if (result.status !== 'analyzed' || result.kind !== 'potfUpc') {
      return result;
    }
    return { ...result, flagged: enrichPotfUpcRows(result.flagged, byUpc) };
  });
}
